import { Injectable, PLATFORM_ID, inject } from '@angular/core';

declare const ngDevMode: boolean | undefined;
import { isPlatformServer } from '@angular/common';
import {
    HttpEvent,
    HttpHandler,
    HttpHeaders,
    HttpInterceptor,
    HttpRequest,
    HttpResponse,
} from '@angular/common/http';
import { TransferState, makeStateKey, StateKey } from '@angular/core';
import { Observable, of } from 'rxjs';
import { tap } from 'rxjs/operators';
import { environment } from '@env/environment';
import { AUTH_CREDENTIALS, NO_TRANSFER_CACHE } from '../interceptors/http-context.tokens';
import { ApiBaseService } from '../services/api-base.service';

/**
 * OB-7718 фаза 7 — transfer-cache API-ответов (лекало sportplus `sp-api:`).
 *
 * Сервер кладёт тела успешных GET-ответов в TransferState, браузер на
 * гидратационной первичке отдаёт их без сети (ключ одноразовый — повторные
 * запросы того же URL идут в сеть). Встроенный httpTransferCache отключён
 * (withNoHttpTransferCache) — нужен контроль ниже.
 *
 * Правила:
 *  - только GET + responseType json + URL API-домена;
 *  - фаза 10: авторизованные ответы (AUTHED_REQ) ТОЖЕ кэшируются — SSR теперь
 *    рендерит identity пользователя, HTML приватный (Cache-Control: private,
 *    no-store в server.ts), без реплея браузер перефетчил бы персональные
 *    данные и разрушил гидратацию. Сверка «тот же юзер» при реплее не нужна:
 *    документ одноразовый и персональный, identity и ovb-api-данные едут в нём
 *    вместе. НЕ кэшируются: AUTH_CREDENTIALS (login/refresh/logout — токены и
 *    Set-Cookie), NO_TRANSFER_CACHE (точечный запрет, напр. /v6/user/me с
 *    сырым token в теле) и не-API credentialed-запросы (withCredentials);
 *  - ключ БЕЗ origin (доменные различия SSR/браузера не ломают совпадение);
 *  - реплей отдаёт structuredClone: легаси-хелперы мутируют тела ответов,
 *    а исходник в TransferState должен остаться нетронутым до сериализации;
 *  - интерцептор ЗАРЕГИСТРИРОВАН ПОСЛЕ HttpNotify (':silent:' уже вырезан из
 *    URL — ключи сервера и браузера совпадают) и ДО серверного JsonFile
 *    (ответы локальных *.json тоже попадают в кэш — аналог sp-fs).
 */
// фаза 10: заголовки, переживающие реплей. Потребители observe:'response'
// читают пагинацию (resolveHdrs) — без неё успешный реплей давал page=NaN и
// ломал автоподгрузку. Списком, а не все: date/server/cors — шум в ng-state.
const REPLAY_HEADERS = [
    'x-pagination-per-page', 'x-pagination-total-count',
    'x-pagination-current-page', 'x-pagination-page-count',
    'x-vote-key', 'x-share-key', 'x-act',
];

@Injectable()
export class ApiTransferCacheInterceptor implements HttpInterceptor {
    private ts = inject(TransferState);
    private apiBase = inject(ApiBaseService);
    private isServer = isPlatformServer(inject(PLATFORM_ID));

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (req.method !== 'GET' || req.responseType !== 'json' || req.withCredentials
            || req.context.get(NO_TRANSFER_CACHE) || req.context.get(AUTH_CREDENTIALS)) {
            return next.handle(req);
        }
        // фаза 11: база платформенно-зависима (браузер '/api', SSR абсолютный) —
        // остаток после slice одинаков, ключи SSR и гидратации совпадают
        const api = this.apiBase.base;
        let key: StateKey<any>;
        if (req.urlWithParams.startsWith(api)) {
            key = makeStateKey<any>('ovb-api:' + req.urlWithParams.slice(api.length));
        } else if (/\/l10n-4\/[^?]+\.json/.test(req.url)) {
            // OB-7718 фаза 7: словари transloco (CDN /l10n-4/{lang}.json) — без
            // реплея клиент фетчит их по сети, selectTranslate эмитит асинхронно,
            // и transloco-блоки (футер/шапка) на первом рендере пусты → их
            // SSR-вью не клеймится и сносится cleanup-ом. С реплеем цепочка
            // HttpClient(of) → transloco.load → selectTranslate синхронна и
            // embedded view создаётся в creation-pass — гидратация неразрушающая.
            // Ключ без origin и ?v= (версия сборки SSR и браузера совпадает).
            key = makeStateKey<any>('ovb-l10n:' + req.url.replace(/^.*\/l10n-4\//, '').replace(/\?.*$/, ''));
        } else {
            return next.handle(req);
        }

        if (this.isServer) {
            return next.handle(req).pipe(tap((event) => {
                if (event instanceof HttpResponse && event.status === 200) {
                    const h: {[name: string]: string} = {};
                    for (const name of REPLAY_HEADERS) {
                        const v = event.headers.get(name);
                        if (v !== null) {
                            h[name] = v;
                        }
                    }
                    // снапшот: ts.set хранит ССЫЛКУ, а компоненты мутируют тела
                    // ответов (взаимные ссылки → циклы → toJson() падал бы при
                    // сериализации) — урок sportplus
                    try {
                        this.ts.set(key, structuredClone({ b: event.body, h }));
                    } catch (e) { /* не-клонируемое тело — просто не кэшируем */ }
                }
            }));
        }

        if (this.ts.hasKey(key)) {
            const cached = this.ts.get(key, null);
            this.ts.remove(key);
            (typeof ngDevMode === 'undefined' || ngDevMode) && console.debug('[transfer-cache] replay', String(key));
            if (cached && (cached.b !== undefined)) {
                return of(new HttpResponse({
                    status: 200,
                    url: req.urlWithParams,
                    body: structuredClone(cached.b),
                    headers: new HttpHeaders(cached.h || {}),
                }));
            }
        }
        return next.handle(req);
    }
}
